import { createContext, useContext, useState } from 'react'

import { handleListingsPageBreadcrumbClick } from '@/utils/tracking/eventsHandlers'

export type SaveSearchButtonState = 'default' | 'saved'

type SelectedAggregate = {
  address: string
  aggregateLength: number
}

type ContextValue = {
  aggregate: {
    isAggregateListOpen: boolean
    selectedAggregate: SelectedAggregate
    setAggregateListOpen: (isAggregateListOpen: boolean) => void
    setSelectedAggregate: (selectedAggregate: SelectedAggregate) => void
  }
  closeBreadcrumbModal: (trackingLabel?: string, eventValue?: string) => void
  closeFilterModal: VoidFunction
  filters: {
    hasJustAppliedFilters: boolean
    setHasJustAppliedFilters: (hasJustAppliedFilters: boolean) => void
  }
  isBreadcrumbModalOpen: boolean
  isFilterModalOpen: boolean
  mapState: {
    isMapFullPage: boolean
    scrollPosition: number
    setMapFullPage: (isMapFullPage: boolean) => void
    setScrollPosition: (scrollPosition: number) => void
  }
  openBreadcrumbModal: (trackingLabel?: string) => void
  openFilterModal: VoidFunction
  saveSearchButtonState: SaveSearchButtonState
  setSaveSearchButtonState: (state: SaveSearchButtonState) => void
}

const contextValue: ContextValue = {
  aggregate: {
    isAggregateListOpen: false,
    selectedAggregate: { address: '', aggregateLength: 0 },
    setAggregateListOpen: () => undefined,
    setSelectedAggregate: () => undefined,
  },
  closeBreadcrumbModal: () => undefined,
  closeFilterModal: () => undefined,
  filters: {
    hasJustAppliedFilters: false,
    setHasJustAppliedFilters: () => undefined,
  },
  isBreadcrumbModalOpen: false,
  isFilterModalOpen: false,
  mapState: {
    isMapFullPage: false,
    scrollPosition: 0,
    setMapFullPage: () => undefined,
    setScrollPosition: () => undefined,
  },
  openBreadcrumbModal: () => undefined,
  openFilterModal: () => undefined,
  saveSearchButtonState: 'default',
  setSaveSearchButtonState: () => undefined,
}

export const CityPageContext = createContext<ContextValue>(contextValue)

export const useCityPage = (): ContextValue => {
  const context = useContext(CityPageContext)

  if (!context) {
    throw new Error(`useCityPage must be used within a CityPageContextProvider`)
  }

  return context
}

export type CityPageContextProps = Partial<ContextValue>

export const CityPageContextProvider: React.FC<CityPageContextProps> = (props) => {
  const [buttonState, setButtonState] = useState<SaveSearchButtonState>('default')
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false)
  const [isBreadcrumbModalOpen, setIsBreadcrumbModalOpen] = useState(false)
  const [isAggregateListOpen, setAggregateListOpen] = useState(false)
  const [selectedAggregate, setSelectedAggregate] = useState({ address: '', aggregateLength: 0 })
  const [isMapFullPage, setMapFullPage] = useState(false)
  const [hasJustAppliedFilters, setHasJustAppliedFilters] = useState(false)
  const [scrollPosition, setScrollPosition] = useState<number | null>(null)

  const openFilterModal = () => setIsFilterModalOpen(true)
  const closeFilterModal = () => setIsFilterModalOpen(false)

  const openBreadcrumbModal = (trackingLabel = 'Open') => {
    setIsBreadcrumbModalOpen(true)
    handleListingsPageBreadcrumbClick(trackingLabel)
  }

  const closeBreadcrumbModal = (trackingLabel = 'Close', eventValue) => {
    setIsBreadcrumbModalOpen(false)
    handleListingsPageBreadcrumbClick(trackingLabel, eventValue)
  }

  return (
    <CityPageContext.Provider
      value={{
        ...contextValue,
        aggregate: {
          isAggregateListOpen,
          selectedAggregate,
          setAggregateListOpen,
          setSelectedAggregate,
        },
        closeBreadcrumbModal,
        closeFilterModal,
        filters: {
          hasJustAppliedFilters,
          setHasJustAppliedFilters,
        },
        isBreadcrumbModalOpen,
        isFilterModalOpen,
        mapState: {
          isMapFullPage,
          scrollPosition,
          setMapFullPage,
          setScrollPosition,
        },
        openBreadcrumbModal,
        openFilterModal,
        saveSearchButtonState: buttonState,
        setSaveSearchButtonState: setButtonState,
        ...props,
      }}
    >
      {props.children}
    </CityPageContext.Provider>
  )
}
